import React, { useState } from 'react';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';

import { useApiClient, useToastService } from '@@common';

import { ApiKeyClient, CreateApiKeyCommand } from '../../../api/api';

interface CreateApiKeyDialogProps {
	open: boolean;
	onClose: () => void;
	onCreated: (apiKeyId: string) => void;
}

export default function CreateApiKeyDialog({ open, onClose, onCreated }: CreateApiKeyDialogProps) {
	const client = useApiClient(ApiKeyClient) as ApiKeyClient;
	const toastService = useToastService();
	const [name, setName] = useState('');
	const [code, setCode] = useState('');
	const [isCodeManuallyEdited, setIsCodeManuallyEdited] = useState(false);
	const [description, setDescription] = useState('');
	const [isSaving, setIsSaving] = useState(false);

	const reset = () => {
		setName('');
		setCode('');
		setIsCodeManuallyEdited(false);
		setDescription('');
	};

	const handleClose = () => {
		if (isSaving) return;
		reset();
		onClose();
	};

	const handleCreate = async () => {
		if (!name.trim() || !code.trim()) return;

		setIsSaving(true);
		try {
			const request: CreateApiKeyCommand = {
				name: name.trim(),
				code: code.trim(),
				description: description.trim() || undefined,
			};
			const apiKeyId = await client.create(request);
			toastService.success(`API key "${request.name}" created successfully.`);
			reset();
			onCreated(apiKeyId);
		} catch (error) {
			console.error('Error creating API key:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to create API key: ${errorMessage}`);
		} finally {
			setIsSaving(false);
		}
	};

	const handleNameChange = (value: string) => {
		setName(value);
		if (!isCodeManuallyEdited) {
			setCode(toCode(value));
		}
	};

	const handleCodeChange = (value: string) => {
		setCode(value);
		setIsCodeManuallyEdited(true);
	};

	return (
		<Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
			<DialogTitle>Create API Key</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ pt: 1 }}>
					<TextField label="Name" value={name} onChange={(event) => handleNameChange(event.target.value)} autoFocus required fullWidth />
					<TextField label="Code" value={code} onChange={(event) => handleCodeChange(event.target.value)} required fullWidth />
					<TextField label="Description" value={description} onChange={(event) => setDescription(event.target.value)} multiline minRows={3} fullWidth />
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={isSaving}>
					Cancel
				</Button>
				<Button onClick={handleCreate} disabled={isSaving || !name.trim() || !code.trim()} variant="contained">
					{isSaving ? 'Creating...' : 'Create'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

function toCode(value: string) {
	return value
		.trim()
		.replace(/([a-z0-9])([A-Z])/g, '$1-$2')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');
}
